import React, { useContext, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AppContext } from '../../context';
import AccreditationBodies from './AccreditationBodies';
import FeaturesManager from 'utils/features/FeaturesManager';
import { AWB_NAV_ITEMS } from './navItems';
import Accreditation from './Accreditation';
import Settings from './Settings';
import useEnabledVersionsInfo from './Accreditation/_hooks/useEnabledVersionsInfo';

FeaturesManager.registerFeature({
  id: 'awb',
  name: 'AWB',
  description: 'Enable AWB Work in Progress',
  enabled: false,
  maxEnv: 'prod',
});

FeaturesManager.registerFeature({
  id: 'awbRUIData',
  name: 'AWB RUI Data',
  description: 'Enable AWB RUI Data - uses LexStaff API instead of org API',
  enabled: false,
  maxEnv: 'prod',
});

export default function AWB() {
  const {
    hasPermission,
    hasAppPermission,
    isLexipolStaff,
    isFeatureEnabled,
    enforcePermissions,
    enforceAppPermissions,
    setCurrentModule,
    registerNavItems,
    currentProfile,
    currentOrganizationId,
  } = useContext(AppContext);

  const shouldFetchVersions = !isLexipolStaff && hasPermission('listAccreditingBodies', currentProfile?.organization?.id || '');
  const versionsInfo = useEnabledVersionsInfo(shouldFetchVersions ? (currentOrganizationId || '') : '');

  const getDefaultRoute = () => {
    if (hasAppPermission('lexipolAccreditationManagerActions') && isLexipolStaff) {
      return <Navigate to='./accrediting-bodies' />;
    }

    if (hasPermission('listAccreditingBodies', currentProfile?.organization?.id || '')) {
      if (versionsInfo.isLoading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        );
      }

      const { hasNoEnabledVersions, hasSingleBody, hasSingleEnabledVersion, singleVersion, singleBody } = versionsInfo;

      if (hasNoEnabledVersions) {
        return <Navigate to='./settings' />;
      }

      if (hasSingleBody && hasSingleEnabledVersion && singleVersion && singleBody) {
        return <Navigate to={`./accreditation/${singleBody.id}/versions/${singleVersion.versionId}/standards`} />;
      }

      if (hasSingleBody && singleBody) {
        return <Navigate to={`./accreditation/${singleBody.id}/versions`} />;
      }

      return <Navigate to='./accreditation' />;
    }

    if (isFeatureEnabled('awbRUIData') && hasAppPermission('lexipolAccreditationManagerActions')) {
      return <Navigate to='./accreditation' />;
    }

    return <Navigate to='/dashboard' />;
  };

  useEffect(() => {
    setCurrentModule('awb');
    registerNavItems('awb', AWB_NAV_ITEMS);
  }, [setCurrentModule, registerNavItems]);

  return (
    <Routes>
      <Route
        path=''
        element={getDefaultRoute()}
      />
      <Route
        path='accrediting-bodies/*'
        element={enforceAppPermissions(['lexipolAccreditationManagerActions'], <AccreditationBodies />)}
      />
      <Route
        path='accreditation/*'
        element={
          isFeatureEnabled('awbRUIData') && hasAppPermission('lexipolAccreditationManagerActions') ? (
            <Accreditation />
          ) : (
            enforcePermissions(['listAccreditingBodies'], <Accreditation />)
          )
        }
      />
      <Route
        path='settings/*'
        element={
          hasPermission('acceptAccreditationBodyVersion', currentProfile?.organization?.id || '') ? (
            <Settings />
          ) : (
            <Navigate to='/' />
          )
        }
      />
    </Routes>
  );
}
